/* ------------------------------------------------------------------
   CUSTOM ENGRAVING — native replacement for Easify Product Options
   --------------------------------------------------------------------
   Modal edition:
   - "Add engraving" (.pdp-personalise) opens a popup with a front-on
     bracelet image and a live curved-text preview (SVG textPath).
   - Confirm closes the popup; the button then shows the engraving text
     with a tick, plus a font/price line and a Remove link.
   - On add-to-cart with a confirmed engraving, intercepts the product
     form and does a multi-item /cart/add.js:
       1) the bracelet line, with engraving details as line-item properties
       2) the existing $20 "Custom Engraving" product (ENG-ADDON-001)

   PROPERTY KEYS below match Easify's exact order output (leading "_" +
   trailing spaces) so the data the 3PL receives is byte-identical.
   Do not rename without confirming the 3PL parsing.
------------------------------------------------------------------ */
(function () {
  var PROP_KEYS = {
    summary: '_Add engraving',
    font: '_Font selection',
    text: '_Engraving text ',        // leading "_" + trailing space — matches Easify exactly
    confirm: '_Confirmation ',       // leading "_" + trailing space — matches Easify exactly
    link: '_engraving_group'
  };
  var SUMMARY_VALUE = 'Custom Engraving ($20.00)';
  var CONFIRM_VALUE = 'I verify my engraving text is correct. I understand customised bracelets are unable to be returned. ';

  var FONT_STACKS = {
    Classic: "'Times New Roman', Times, serif",
    Script: "'Snell Roundhand', 'SnellRoundhand', 'Apple Chancery', 'Segoe Script', cursive"
  };

  customElements.define('custom-engraving', class extends HTMLElement {
    connectedCallback() {
      this.variantId = parseInt(this.dataset.engravingVariantId, 10);
      this.maxLen = parseInt(this.dataset.maxLength, 10) || 19;
      // draft = what's in the open modal; saved = the locked-in engraving (null if none)
      this.draft = { font: null, text: '', confirmed: false };
      this.saved = null;

      // The modal lives inside a hidden block wrapper — move it to <body>
      // so it can actually display.
      this.modal = this.querySelector('[data-ce-modal]');
      document.body.appendChild(this.modal);

      this.textInput = this.modal.querySelector('[data-ce-text]');
      this.countEl = this.modal.querySelector('[data-ce-count]');
      this.confirmInput = this.modal.querySelector('[data-ce-confirm]');
      this.textPathEl = this.modal.querySelector('[data-ce-textpath]');
      this.prevTextEl = this.modal.querySelector('[data-ce-prevtext]');
      this.removeBtn = this.modal.querySelector('[data-ce-remove]');

      this.personaliseBtn = document.querySelector('.pdp-personalise');
      if (this.personaliseBtn) {
        this.defaultBtnHTML = this.personaliseBtn.innerHTML;
        // Capture-phase so the theme's own dropdown toggle never runs.
        var self = this;
        this.personaliseBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.openModal();
        }, { capture: true });
      }

      // Persist across page bounces (metal switcher navigates between products)
      this.storeKey = 'boobie-engraving-' + this.dataset.productId;
      try {
        var stored = sessionStorage.getItem(this.storeKey);
        if (stored) { this.saved = JSON.parse(stored); }
      } catch (e) { /* private mode etc */ }

      this.bindModal();
      this.renderPreview();
      if (this.saved) this.paintButton();

      // Bind to THIS product's form specifically so we never grab an upsell form.
      this.productForm =
        this.closest('product-form') ||
        document.querySelector('product-form[data-product-id="' + this.dataset.productId + '"]') ||
        document.querySelector('product-form');
      if (this.productForm) {
        this.productForm.addEventListener('submit', this.onSubmit.bind(this), { capture: true });
      }
    }

    /* ---------------- modal ---------------- */

    openModal() {
      // Start the draft from the saved engraving (edit) or fresh.
      var src = this.saved || { font: null, text: '', confirmed: false };
      this.draft = { font: src.font, text: src.text, confirmed: src.confirmed };

      this.textInput.value = this.draft.text;
      this.confirmInput.checked = this.draft.confirmed;
      this.syncFontButtons();
      this.updateCount();
      this.renderPreview();
      this.removeBtn.hidden = !this.saved;
      ['font', 'text', 'confirm'].forEach(this.clearError.bind(this));

      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var ti = this.textInput;
      setTimeout(function () { ti.focus(); }, 60);
    }

    closeModal() {
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    bindModal() {
      var self = this;
      this.modal.querySelectorAll('[data-ce-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.closeModal(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.modal.hidden) self.closeModal();
      });

      this.modal.querySelectorAll('[data-ce-font]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.draft.font = btn.dataset.ceFont;
          self.syncFontButtons();
          self.clearError('font');
          self.renderPreview();
        });
      });

      this.textInput.addEventListener('input', function () {
        self.draft.text = self.textInput.value;
        self.updateCount();
        self.clearError('text');
        self.renderPreview();
      });

      this.confirmInput.addEventListener('change', function () {
        self.draft.confirmed = self.confirmInput.checked;
        self.clearError('confirm');
      });

      this.modal.querySelector('[data-ce-confirm-btn]').addEventListener('click', function (e) {
        if (!self.validateDraft()) {
          var firstErr = self.modal.querySelector('.ce__error.is-shown');
          if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        self.saved = { font: self.draft.font, text: self.draft.text.trim(), confirmed: true };
        self.persist();
        self.paintButton();
        // "Confirm & add to bag": the confirm also adds the engraved bracelet
        // to the cart - addWithEngraving() opens the drawer on success.
        var btn = e.currentTarget;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        self.addWithEngraving().finally(function () {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          self.closeModal();
        });
      });

      this.removeBtn.addEventListener('click', function () {
        self.saved = null;
        self.persist();
        self.paintButton();
        self.closeModal();
      });
    }

    syncFontButtons() {
      var self = this;
      this.modal.querySelectorAll('[data-ce-font]').forEach(function (b) {
        b.classList.toggle('is-selected', b.dataset.ceFont === self.draft.font);
      });
    }

    updateCount() {
      this.countEl.textContent = this.textInput.value.length + '/' + this.maxLen;
    }

    /* ---------------- curved live preview ---------------- */

    renderPreview() {
      var text = (this.draft.text || '').slice(0, this.maxLen);
      var empty = text.length === 0;
      this.textPathEl.textContent = empty ? 'Your text' : text;
      if (empty) this.prevTextEl.setAttribute('data-ce-empty', '');
      else this.prevTextEl.removeAttribute('data-ce-empty');

      var font = this.draft.font || 'Classic';
      this.prevTextEl.style.fontFamily = FONT_STACKS[font] || FONT_STACKS.Classic;

      // Constant size — the arc is long enough for the full 19 characters.
      this.prevTextEl.style.fontSize = '46px';
      this.textPathEl.setAttribute('startOffset', '50%');
      this.textPathEl.style.textAnchor = 'middle';
    }

    /* ---------------- button state ---------------- */

    paintButton() {
      var btn = this.personaliseBtn;
      if (!btn) return;
      var old = document.querySelector('.pdp-personalise-meta');
      if (old) old.remove();

      if (!this.saved) {
        btn.classList.remove('pdp-personalise--set');
        btn.innerHTML = this.defaultBtnHTML;
        return;
      }

      btn.classList.add('pdp-personalise--set');
      btn.innerHTML = '';
      var val = document.createElement('span');
      val.className = 'pdp-personalise__val';
      val.textContent = '“' + this.saved.text + '”';
      btn.appendChild(val);

      var meta = document.createElement('div');
      meta.className = 'pdp-personalise-meta';
      var label = document.createElement('span');
      if (this.dataset.freeEngraving === 'true') {
        label.textContent = 'Engraving confirmed · Free';
      } else {
        var engPrice = this.dataset.engravingPrice || '$20.00';
        label.textContent = 'Engraving confirmed · +' + engPrice;
      }
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.textContent = 'Remove';
      var self = this;
      rm.addEventListener('click', function () {
        self.saved = null;
        self.persist();
        self.paintButton();
      });
      meta.appendChild(label);
      meta.appendChild(rm);
      btn.insertAdjacentElement('afterend', meta);
    }

    persist() {
      try {
        if (this.saved) sessionStorage.setItem(this.storeKey, JSON.stringify(this.saved));
        else sessionStorage.removeItem(this.storeKey);
      } catch (e) { /* fail quiet */ }
    }

    /* ---------------- validation ---------------- */

    showError(key) {
      var el = this.modal.querySelector('[data-ce-error="' + key + '"]');
      if (el) el.classList.add('is-shown');
    }
    clearError(key) {
      var el = this.modal.querySelector('[data-ce-error="' + key + '"]');
      if (el) el.classList.remove('is-shown');
    }

    validateDraft() {
      var ok = true;
      if (!this.draft.font) { this.showError('font'); ok = false; }
      if (!this.draft.text.trim()) { this.showError('text'); ok = false; }
      if (!this.draft.confirmed) { this.showError('confirm'); ok = false; }
      return ok;
    }

    /* ---------------- add to cart ---------------- */

    onSubmit(event) {
      // No confirmed engraving → normal single-item add, untouched.
      if (!this.saved) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addWithEngraving();
    }

    async addWithEngraving() {
      // Re-entry guard: a second tap (confirm button or Add to Bag) while the
      // first add is in flight must not double-add the bracelet.
      if (this._adding) return;
      this._adding = true;
      var form = this.productForm.querySelector('form[action*="/cart/add"]');
      var braceletVariant = form.querySelector('[name="id"]').value;
      var price = this.dataset.engravingPrice || '$20.00';

      // ENGRAVING AS ITS OWN LINE (17 Sep 2026). We no longer hand the price to
      // the Easify cart transform. That transform took the hardcoded "20" in
      // _po:items:addon, treated it as AUD and FX-converted it, so every non-AU
      // market was mischarged (US $15.45 not $16, CA $20.29 not $20, UK 11.80
      // not 12). Instead the real Custom Engraving variant is added as a second
      // cart line, so Shopify charges the price actually set for that market.
      // The two lines are tied together by _engraving_group; engraving-cart-sync.js
      // keeps their quantities in step and removes the charge if the bracelet
      // goes, and line-item.liquid hides the charge line in the drawer.
      // The VISIBLE keys the 3PL reads for the text are unchanged.
      var props = {};
      var freeEngravingLine = null;
      var paidEngravingLine = null;
      if (this.dataset.freeEngraving === 'true') {
        // FREE heirloom engraving: unchanged. The $0 "Sterling Complimentary
        // Engraving" line is what triggers the "Engraving" order tag and puts
        // an engraving line in front of the 3PL (orders 21-29 Jul shipped
        // untagged without it). Hidden in the drawer by line-item.liquid.
        props['_Add engraving'] = 'Custom Engraving (Free)';
        props['_Font selection'] = this.saved.font;
        props['_Engraving text '] = this.saved.text;
        props['_Confirmation '] = CONFIRM_VALUE;
        props['Engraving'] = '\u201C' + this.saved.text + '\u201D \u00B7 ' + this.saved.font;
        freeEngravingLine = {
          id: 45408045236364,
          quantity: 1,
          properties: { _tpo_option_value_id: '17336112' }
        };
      } else {
        var group = 'eng-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
        // Same hidden keys as every live engraved order already carries.
        props['_Add engraving'] = 'Custom Engraving (' + price + ')';
        props['_Font selection'] = this.saved.font;
        props['_Engraving text '] = this.saved.text;
        props['_Confirmation '] = CONFIRM_VALUE;
        props['_engraving_group'] = group;
        // Visible summary, same key the free sterling path already uses. The
        // 17 Sep move off Easify dropped it, so the order confirmation email
        // showed a "Custom Engraving" charge with no text and customers wrote
        // in unable to check their engraving (#bb19047, #bb19068). Shopify's
        // notification shows every non-underscore property under the line.
        props['Engraving'] = '\u201C' + this.saved.text + '\u201D \u00B7 ' + this.saved.font;
        paidEngravingLine = {
          id: this.variantId,
          quantity: 1,
          properties: { _engraving_group: group }
        };
      }

      var submitBtns = Array.from(form.elements).filter(function (el) { return el.type === 'submit'; });
      submitBtns.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });

      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true, detail: { sections: sectionsToBundle }
      }));

      try {
        var res = await fetch(Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: braceletVariant, quantity: 1, properties: props }]
              .concat(freeEngravingLine ? [freeEngravingLine] : [])
              .concat(paidEngravingLine ? [paidEngravingLine] : []),
            sections: sectionsToBundle.join(','),
            sections_url: Shopify.routes.root + 'variants/' + braceletVariant
          })
        });
        var json = await res.json();
        submitBtns.forEach(function (b) { b.removeAttribute('aria-busy'); });

        if (!res.ok) {
          form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: json.description } }));
          return;
        }
        // Engraving is now in the bag — reset the button for a fresh one.
        this.saved = null;
        this.persist();
        this.paintButton();
        var cart = await (await fetch(Shopify.routes.root + 'cart.js')).json();
        cart.sections = json.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cart }
        }));
      } catch (e) {
        submitBtns.forEach(function (b) { b.removeAttribute('aria-busy'); });
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this._adding = false;
      }
    }
  });
})();